import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { images } from '../utils/images';
import { CheckCircle2, Phone, Palette, Music, Waves, TreePine, Users, Dumbbell } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import VideoBackground from '../components/VideoBackground';
import SEO from '../components/SEO';

const RecreationNdisPage = () => {
  const faqs = [
    {
      question: "What recreational activities does the NDIS fund?",
      answer: "The NDIS can fund recreational activities that help you achieve your goals, build social connections, and develop skills. This includes sports programs, art and music sessions, outdoor adventures, swimming, gym memberships with support workers, and community group activities."
    },
    {
      question: "How do I include recreation supports in my NDIS plan?",
      answer: "Discuss your recreational goals with your Local Area Coordinator or NDIA planner during your plan review. Explain how specific activities help you build skills, social connections, or improve your health. Our team can help you articulate these goals effectively."
    },
    {
      question: "Can I choose which activities I participate in?",
      answer: "Absolutely. The NDIS is built on choice and control. You decide which recreational activities align with your interests, goals, and preferences. We work with you to find activities you'll enjoy and benefit from."
    },
    {
      question: "Do I need a support worker to attend recreational activities?",
      answer: "It depends on your support needs and NDIS plan. Some participants attend activities independently, while others may have a support worker accompany them. We tailor the level of support to your individual requirements."
    },
    {
      question: "Are group recreational activities available?",
      answer: "Yes, we offer both individual and group recreational activities. Group settings provide wonderful opportunities to build social skills, make friends, and enjoy shared experiences in a supportive environment."
    },
    {
      question: "Can recreational activities help with my mental health?",
      answer: "Absolutely. Research consistently shows that recreational activities improve mental health by reducing stress, combating isolation, boosting mood, and providing a sense of purpose and achievement. Many of our participants report significant improvements in their overall well-being."
    }
  ];

  const scrollToContact = () => { window.location.href = '/contact'; };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Recreation With The NDIS" description="Engaging NDIS recreational programs in Brisbane promoting social inclusion, skill development and well-being. Outdoor adventures, creative arts, sport and more." />
      <Header />

      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ color: '#587740' }} data-testid="recreation-ndis-title">
                Recreation With The NDIS
              </h1>
              <p className="text-lg md:text-xl leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                Life should be about more than just getting by — it should be about enjoying it. Whether it's a day at the beach, painting a picture, kicking a ball, or just being around people who make you smile, recreation is how you discover what makes you feel alive.
              </p>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                In simple terms — we help you find activities you love, connect you with people who share your interests, and make sure you have the support to enjoy every moment. Your NDIS plan can fund this, and we'll show you how.
              </p>
              <p className="text-base leading-relaxed italic mb-4" style={{ color: '#6c935e' }}>
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>
              <p className="text-sm font-semibold mb-8" style={{ color: '#587740' }}>
                Free consultation — let's chat about what brings you joy and how we can make it happen.
              </p>
              <Button onClick={scrollToContact} size="lg" className="text-white hover:opacity-90" style={{ backgroundColor: '#81b956' }} data-testid="recreation-hero-cta">
                <Phone className="w-5 h-5 mr-2" /> Contact Us
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src={images.beach} alt="Beach recreation activities" className="w-full h-[400px] md:h-[500px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center" style={{ color: '#587740' }}>
            Why Recreation Matters
          </h2>
          <p className="text-lg text-center max-w-4xl mx-auto mb-12" style={{ color: '#5e5e5e' }}>
            Recreation isn't just about having fun — although that's a big part of it. It's about building friendships, developing new skills, staying healthy, and finding a sense of belonging. The NDIS understands this, which is why community and social participation is a key part of your plan.
          </p>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                At Life Care and Wellness, we design recreational programs around what you actually enjoy — not what's convenient for us. Many providers offer limited, generic group activities. We take the time to understand your interests, your personality, and your goals, then create opportunities for genuine participation and personal growth.
              </p>
              <ul className="space-y-3">
                {["Outdoor adventures and nature activities", "Creative arts, music, and cultural experiences", "Sport, fitness, and physical well-being", "Social groups and community events", "Life skills through recreational learning"].map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#81b956' }} />
                    <span className="text-base" style={{ color: '#5e5e5e' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img src={images.fishing} alt="Outdoor recreational activities" className="w-full h-[350px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ color: '#587740' }}>
            Recreational Activities We Offer
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: TreePine, title: "Outdoor Adventures", desc: "Explore nature through bushwalking, fishing trips, beach outings, and park activities that build confidence and connect you with the natural world." },
              { icon: Palette, title: "Creative Arts", desc: "Express yourself through painting, drawing, pottery, and other creative activities that develop fine motor skills and provide a wonderful sense of achievement." },
              { icon: Music, title: "Music & Performance", desc: "Discover the joy of music through group sessions, instrument lessons, and performance opportunities that build communication and social skills." },
              { icon: Dumbbell, title: "Sport & Fitness", desc: "Stay active and healthy with inclusive sports programs, gym sessions, swimming, and group fitness activities tailored to all ability levels." },
              { icon: Users, title: "Social Groups", desc: "Join community groups, attend local events, and build lasting friendships through regular social activities in a supportive environment." },
              { icon: Waves, title: "Aquatic Activities", desc: "Enjoy swimming, water sports, and beach activities that provide excellent exercise, sensory experiences, and a refreshing way to stay active." }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Card key={i} className="border-none shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#AABC5A' }}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3" style={{ color: '#587740' }}>{item.title}</h3>
                    <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>{item.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img src={images.cooking} alt="Life skills through recreation" className="w-full h-[350px] object-cover" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#587740' }}>
                Building Skills Through Recreation
              </h2>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#5e5e5e' }}>
                Every recreational activity is an opportunity to develop important life skills. Through our programs, participants build:
              </p>
              <ul className="space-y-3">
                {["Communication and teamwork abilities", "Problem-solving and decision-making skills", "Physical fitness and coordination", "Self-confidence and independence", "Time management and planning"].map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#81b956' }} />
                    <span className="text-base" style={{ color: '#5e5e5e' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }} data-testid="recreation-faq">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12 text-center" style={{ color: '#587740' }}>
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border rounded-lg px-6 shadow-sm bg-white" data-testid={`recreation-faq-${i}`}>
                <AccordionTrigger className="text-left hover:no-underline py-6" style={{ color: '#587740' }}>
                  <span className="text-lg font-semibold pr-4">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed pb-6" style={{ color: '#5e5e5e' }}>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-12">
            <Button onClick={scrollToContact} size="lg" className="text-white hover:opacity-90" style={{ backgroundColor: '#81b956' }}>
              <Phone className="w-5 h-5 mr-2" /> Contact Us
            </Button>
          </div>
        </div>
      </section>

      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Life Is Meant to Be Enjoyed — Let Us Help You Live It</h2>
          <p className="text-lg mb-8 text-white/90">Discover activities that excite you, connect with people who care, and start building a life full of joy and purpose. Free consultation, free action plan — let's find what makes you happy.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="tel:+611300404229">
            <Button size="lg" className="text-base px-8 py-6" style={{ backgroundColor: '#FFFFFF', color: '#587740' }}>
              <Phone className="w-5 h-5 mr-2" /> Call Us Now
            </Button>
          </a>
          <a href="/get-on-ndis-free">
            <Button size="lg" variant="outline" className="text-base px-8 py-6 border-2 border-white text-white hover:bg-white/10">
              Get on the NDIS Free
            </Button>
          </a>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default RecreationNdisPage;
